import { FieldValue, Timestamp } from 'firebase/firestore';
import { Descendant } from 'slate';

export enum ECollections {
  users = 'users',
  trips = 'trips',
  cards = 'cards',
  events = 'events',
  lists = 'lists',
  days = 'days',
  dividers = 'dividers',
}

/** `users/{uid}` document. Legacy documents may lack the newer fields until their owner signs in. */
export type UserData = {
  createdAt: Date;
  /** Shown across the app. Null only on legacy documents not yet synced. */
  displayName: string | null;
  /** Set once at creation; never changes. */
  originalDisplayName?: string;
  /** Copy of the Auth email, kept in step on sign-in. May be missing until the owner signs in. */
  email?: string;
  /** Profile bio, set from the profile page (max 500 characters, enforced by the rules). */
  description?: string;
  /** Written only by the deactivateAccount Cloud Function. */
  status?: 'deactivated';
};

/** A trip member as shown in the share dialog: their user document plus their uid. */
export type TripMemberDetails = {
  userId: string;
  displayName: string;
  /** Empty when the user document has no stored email yet. */
  email: string;
  status: 'active' | 'deactivated';
};

export type LatLng = {
  latitude: number;
  longitude: number;
};

export enum TripRole {
  OWNER = 'owner',
  EDITOR = 'editor',
  VIEWER = 'viewer',
}

export interface RoleValidation {
  canAssign: TripRole[];
  cannotModify: TripRole[];
}

export const roleValidations: Record<TripRole, RoleValidation> = {
  [TripRole.OWNER]: {
    canAssign: [TripRole.OWNER, TripRole.EDITOR, TripRole.VIEWER],
    cannotModify: [],
  },
  [TripRole.EDITOR]: {
    canAssign: [TripRole.EDITOR, TripRole.VIEWER],
    cannotModify: [TripRole.OWNER],
  },
  [TripRole.VIEWER]: {
    canAssign: [],
    cannotModify: [TripRole.OWNER, TripRole.EDITOR, TripRole.VIEWER],
  },
};

export const canModifyUserRole = (
  currentUserRole: TripRole,
  targetUserId: string,
  newRole: TripRole,
  roles: RolesMap
): boolean => {
  const validation = roleValidations[currentUserRole];
  const isTargetOwner = roles[targetUserId] === TripRole.OWNER;

  if (isTargetOwner) {
    return false;
  }

  if (!validation.canAssign.includes(newRole)) {
    return false;
  }

  return true;
};

export interface UserToRole {
  userId: string;
  role: TripRole;
}

export interface RolesMap {
  [key: string]: TripRole;
}

export interface DeletableRolesMap {
  [key: string]: TripRole | FieldValue;
}

export interface TripMemberData {
  userId: string;
  role: TripRole;
  joinedAt: Timestamp;
  invitedBy?: string | null;
}

export interface NewTripData {
  title: string;
  location: LatLng;
  place: string;
  notes: Descendant[];
  startDateISO?: string;
  endDateISO?: string;
  createdAt: Timestamp | FieldValue;
}

export interface LastOpenedMap {
  [userId: string]: Timestamp;
}

export interface TripData {
  listOrder: string[];
  location: LatLng;
  notes: Descendant[];
  place: string;
  public: boolean;
  roles: RolesMap;
  title: string;
  createdAt: Timestamp;
  startDateISO?: string;
  endDateISO?: string;
  lastOpened?: LastOpenedMap;
}

export interface ListData {
  cardOrder?: string[];
  creator?: string;
  emoji: string;
  name: string;
  createdAt?: Timestamp;
}

export interface DividerData {
  emoji: string;
  name: string;
  listOrder: string[];
  createdAt?: Timestamp;
}

export interface CardData extends Record<string, unknown> {
  addressComponents?: google.maps.GeocoderAddressComponent[];
  adrAddress?: string;
  businessStatus?: google.maps.places.BusinessStatus;
  creator: string;
  formattedAddress?: string;
  formattedPhoneNumber?: string;
  googlePlaceId: string;
  internationalPhoneNumber?: string;
  location?: LatLng;
  name?: string;
  notes?: Descendant[];
  openingHours?: string[];
  priceLevel?: number;
  rating?: number;
  userRatingsTotal?: number;
  placeType?: string;
  types?: string[];
  url?: string;
  website?: string;
  dateAdded?: Timestamp;
  defaultImage?: number | null;
  reactions?: ReactionData;
  photoReferences?: string[];
}

export interface DayData {
  dateISO: string;
  notes?: Descendant[];
  eventOrder?: string[];
}

export interface EventData {
  cardId: string;
  startDateISO: string;
  endDateISO?: string;
}

export enum ReactionType {
  MUST_GO = 3,
  STAR = 2,
  YES = 1,
  MAYBE = 0,
  NO = -1,
}

export interface ReactionData {
  [userId: string]: {
    type: ReactionType;
    timestamp: FieldValue;
    email: string | null;
  };
}
